import { type LngLatBounds, type LngLatLike } from 'mapbox-gl';
import { type RefObject, useEffect, useState } from 'react';

import type { MapRef } from 'react-map-gl/mapbox';

import useBreakpoint from '@/hooks/use-break-point';
import usePinnedItem from '@/hooks/use-pinned-item';
import {
  MAP_BOUNDARY_DEFAULT,
  MAP_BOUNDARY_MOBILE,
  MOBILE_INITIAL_ZOOM_OFFSET,
  ZOOM_8,
} from '@/utils/constants/map';

const useMapBounds = (
  mapRef: RefObject<MapRef | null>,
  isMapLoaded: boolean,
  shouldRenderMap: boolean
) => {
  const [initialZoom, setInitialZoom] = useState<number | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);

  const isMobile = useBreakpoint('mobile');
  const isMediumMobile = useBreakpoint('mediumMobile');
  const isLargeScreen = !useBreakpoint('tablet');

  const { pinnedItemId } = usePinnedItem();

  useEffect(() => {
    if (!isMapLoaded || !mapRef.current) {
      return;
    }

    const map = mapRef.current.getMap();

    if (map.getMaxBounds()) {
      setIsConfigured(true);

      return;
    }

    const setupMapView = () => {
      const mapBounds: [LngLatLike, LngLatLike] = isMobile
        ? MAP_BOUNDARY_MOBILE
        : MAP_BOUNDARY_DEFAULT;

      const cameraOptions = map.cameraForBounds(mapBounds, {
        padding: isMediumMobile ? 80 : 100,
      });

      if (!cameraOptions?.center || !cameraOptions?.zoom) {
        return;
      }

      const fitZoom = cameraOptions.zoom;
      const baseZoom = isLargeScreen ? ZOOM_8 : fitZoom;
      const targetInitialZoom = isLargeScreen ? ZOOM_8 : fitZoom + MOBILE_INITIAL_ZOOM_OFFSET;

      setInitialZoom(targetInitialZoom);

      // 1. Jump to the ideal base camera to calculate the correct screen viewport bounds
      map.jumpTo({
        center: cameraOptions.center,
        zoom: baseZoom,
      });

      // 2. Capture and set the exact viewport boundaries so large screens aren't forced to zoom in
      const currentViewBounds = map.getBounds() as LngLatBounds;
      map.setMaxBounds(currentViewBounds);
      map.setMinZoom(baseZoom);

      // 3. If no item is pinned, apply the target initial zoom offset.
      // If an item IS pinned, we skip this so we don't lock the camera before the pin's flyTo animation.
      if (!pinnedItemId && targetInitialZoom !== map.getZoom()) {
        map.jumpTo({
          center: cameraOptions.center,
          zoom: targetInitialZoom,
        });
      }

      // 4. Mark the map bounds as fully configured so MapLayers can safely mount
      setIsConfigured(true);
    };

    const { width } = map.getContainer().getBoundingClientRect();

    if (width === 0) {
      map.resize();
      map.once('resize', setupMapView);
    } else {
      setupMapView();
    }
  }, [isMapLoaded, isMobile, isMediumMobile, mapRef, isLargeScreen, shouldRenderMap, pinnedItemId]);

  return { initialZoom, isConfigured };
};

export default useMapBounds;
